import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Save, Upload } from 'lucide-react';

export const SettingsManager = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    contact_email: '',
    amazon_shop_url: '',
    site_title: '',
    gtm_id: '',
    logo_url: '',
    favicon_url: '',
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings`);
      setFormData(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload`, uploadFormData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setFormData({ ...formData, [fieldName]: response.data.url });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/settings`, formData, {
        headers: getAuthHeader(),
      });
      alert('Settings updated successfully!');
    } catch (error) {
      console.error('Error updating settings:', error);
      alert('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-white">Loading...</div>;
  }

  return (
    <div className="max-w-4xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-6">
          <h3 className="text-xl font-bold text-white mb-4">Site Settings</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Site Title
              </label>
              <input
                type="text"
                value={formData.site_title}
                onChange={(e) =>
                  setFormData({ ...formData, site_title: e.target.value })
                }
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Contact Email
              </label>
              <input
                type="email"
                value={formData.contact_email}
                onChange={(e) =>
                  setFormData({ ...formData, contact_email: e.target.value })
                }
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="distributors@planeskeeper.com"
              />
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Amazon Shop URL
              </label>
              <input
                type="url"
                value={formData.amazon_shop_url}
                onChange={(e) =>
                  setFormData({ ...formData, amazon_shop_url: e.target.value })
                }
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="https://amazon.com/your-store"
              />
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Google Tag Manager ID
              </label>
              <input
                type="text"
                value={formData.gtm_id || ''}
                onChange={(e) =>
                  setFormData({ ...formData, gtm_id: e.target.value })
                }
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="GTM-XXXXXXX"
              />
              <p className="text-xs text-gray-400 mt-1">
                Enter your Google Tag Manager ID (e.g., GTM-XXXXXXX) to enable tracking on public pages
              </p>
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Brand Logo
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'logo_url')}
                className="hidden"
                id="logo-upload"
              />
              <label
                htmlFor="logo-upload"
                className="inline-flex items-center px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white hover:bg-gray-600 cursor-pointer"
              >
                {uploading ? 'Uploading...' : 'Upload Logo'}
              </label>
              {formData.logo_url && (
                <div className="mt-2">
                  <img src={formData.logo_url} alt="Logo" className="h-16 bg-white p-2 rounded" />
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">
                Brand logo displayed in header (recommended: PNG with transparency)
              </p>
            </div>

            <div>
              <label className="block text-white text-sm font-medium mb-2">
                Favicon
              </label>
              <input
                type="file"
                accept="image/x-icon,image/png"
                onChange={(e) => handleImageUpload(e, 'favicon_url')}
                className="hidden"
                id="favicon-upload"
              />
              <label
                htmlFor="favicon-upload"
                className="inline-flex items-center px-4 py-2 bg-gray-700 border border-gray-600 rounded text-white hover:bg-gray-600 cursor-pointer"
              >
                {uploading ? 'Uploading...' : 'Upload Favicon'}
              </label>
              {formData.favicon_url && (
                <div className="mt-2">
                  <img src={formData.favicon_url} alt="Favicon" className="w-8 h-8 bg-white p-1 rounded" />
                </div>
              )}
              <p className="text-xs text-gray-400 mt-1">
                Browser tab icon (recommended: 32x32 or 64x64 .png or .ico)
              </p>
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="flex items-center px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
        >
          <Save className="w-5 h-5 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </form>
    </div>
  );
};